import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Baby, Link2, UserPlus } from "lucide-react";
import { getAuthErrorMessage } from "../LoginForm";
import { HianyzasAdminPending } from "./HianyzasTab";
import { IgenylesPending } from "./IgenylesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const card = "rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100";

function PendingList({ type, title, Icon, fmt }) {
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => axios.get(`${API}/ugyfelkapu/admin/requests`, { params: { token: token() } }).then((r) => setReqs(r.data.requests.filter((x) => x.type === type))).catch(() => setReqs([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (r, approve) => {
    setBusy(r.id);
    try { await axios.post(`${API}/ugyfelkapu/admin/requests/${r.id}/decide`, { approve }, { params: { token: token() } }); toast.success(approve ? "Kérelem jóváhagyva" : "Kérelem elutasítva"); load(); }
    catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy("");
  };
  return (
    <section className={card} id={`kerelmek-${type}`} data-testid={`kerelmek-${type}`}>
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900"><Icon size={20} className="text-yellow-600" /> {title} {reqs && <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-900">{reqs.length}</span>}</h2>
      {!reqs ? <p className="py-4 text-center text-slate-600">Betöltés...</p> : reqs.length === 0 ? <p className="py-4 text-center text-sm text-slate-600">Nincs elbírálásra váró kérelem</p> : (
        <ul className="space-y-3">{reqs.map((r) => (
          <li key={r.id} className="flex flex-col gap-3 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 sm:flex-row sm:items-center" data-testid={`admin-request-${r.id}`}>
            <p className="flex-1 text-sm font-medium text-slate-800">{fmt(r)}</p>
            <div className="flex shrink-0 gap-2">
              <button type="button" disabled={busy === r.id} onClick={() => decide(r, true)} className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid={`approve-btn-${r.id}`}><Check size={16} /> Jóváhagyás</button>
              <button type="button" disabled={busy === r.id} onClick={() => decide(r, false)} className="inline-flex items-center gap-1 rounded-xl bg-red-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60" data-testid={`reject-btn-${r.id}`}><X size={16} /> Elutasítás</button>
            </div>
          </li>
        ))}</ul>
      )}
    </section>
  );
}

function Registrations() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => axios.get(`${API}/ugyfelkapu/admin/registrations`, { params: { token: token() } }).then((r) => setRows(r.data.registrations)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const act = async (r, action) => {
    if (action === "reject" && !window.confirm(`Elutasítod ${r.name} regisztrációját? A fiók és a megadott adatok törlődnek.`)) return;
    setBusy(r.id);
    try { const x = await axios.post(`${API}/ugyfelkapu/admin/registrations/${r.id}/${action}`, {}, { params: { token: token() } }); toast.success(x.data.message); load(); } catch (e) { toast.error(getAuthErrorMessage(e)); }
    setBusy("");
  };
  const g = { ferfi: "férfi", no: "nő" };
  return (
    <section className={card} id="kerelmek-registrations" data-testid="kerelmek-registrations">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900"><UserPlus size={20} className="text-yellow-600" /> Felhasználói regisztrációk {rows && <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-900">{rows.length}</span>}</h2>
      {!rows ? <p className="py-4 text-center text-slate-600">Betöltés...</p> : rows.length === 0 ? <p className="py-4 text-center text-sm text-slate-600">Nincs elbírálásra váró regisztráció</p> : (
        <ul className="space-y-3">{rows.map((r) => (
          <li key={r.id} className="flex flex-col gap-3 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 sm:flex-row sm:items-center" data-testid={`registration-${r.id}`}>
            <div className="flex-1 text-sm text-slate-800">
              <p className="font-semibold text-slate-900">{r.name} <span className="font-mono text-xs font-normal text-slate-700">{r.identifier}</span></p>
              <p>{r.email}{r.phone ? ` · ${r.phone}` : ""}{r.gender ? ` · ${g[r.gender] || r.gender}` : ""}{r.birth_date ? ` · szül. ${r.birth_date}` : ""}</p>
              <p className="text-xs text-slate-700">Regisztrálva: {r.registered_at ? r.registered_at.slice(0, 10).replace(/-/g, ".") + "." : "—"} · Szerepkör: Normál</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" disabled={busy === r.id} onClick={() => act(r, "approve")} className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid={`registration-approve-${r.id}`}><Check size={16} /> Elfogadás</button>
              <button type="button" disabled={busy === r.id} onClick={() => act(r, "reject")} className="inline-flex items-center gap-1 rounded-xl bg-red-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60" data-testid={`registration-reject-${r.id}`}><X size={16} /> Elutasítás</button>
            </div>
          </li>
        ))}</ul>
      )}
    </section>
  );
}

export function KerelmekTab({ focus, isAdmin = false }) {
  useEffect(() => { if (focus) setTimeout(() => document.getElementById(`kerelmek-${focus}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400); }, [focus]);
  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="kerelmek-tab">
      {isAdmin && <Registrations />}
      <PendingList type="link" title="Hozzárendelési kérelmek" Icon={Link2} fmt={(r) => `${r.requester_name} → ${r.target_name} (${r.target_identifier})`} />
      <PendingList type="child" title="Gyerekfiók kérelmek" Icon={Baby} fmt={(r) => `${r.requester_name}: ${r.child_name} · szül. ${r.child_birth_date}`} />
      <div id="kerelmek-hianyzas"><HianyzasAdminPending /></div>
      <IgenylesPending />
    </div>
  );
}

export default KerelmekTab;
